import React, { useMemo } from "react";
import { ConnectedProps, connect } from "react-redux";
import Common from "us.common";
import { useTranslation } from "react-i18next";
import { RootState } from "us.helper/types";
import { DebtorType } from "us.collection.debtor/components/DebtorBasicInfo/Constants";
import {
  getRetainingDebtor,
  getFelidValue,
} from "us.collection.debtor/components/DebtorBasicInfo/Components/DuplicateDebtors/Functions";
import {
  DEBTOR_BASIC_INFO,
  DEBTOR_ADDRESS_INFO,
  PropertyNames,
} from "us.collection.debtor/components/DebtorBasicInfo/Components/DuplicateDebtors/Constants";
import { getRouteUrl } from "us.helper";
import _ from "lodash";

const { $Checkbox, $DateLabel, $Typography } = Common.Components;
const { Paragraph } = $Typography;

/**
 * @description - Retain debtor component to display selected retain debtor data.
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/UnicornBOX/pages/3627384863/Handle+Duplicate+Debtors+UI+Implementation
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 04/08/2024
 */
const RetainDebtor: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation(["US.COLLECTION.DEBTOR"]);

  const { duplicatingDebtors } = props;

  const retainingDebtor = useMemo(() => {
    return getRetainingDebtor(duplicatingDebtors.data);
  }, [duplicatingDebtors.data]);

  const {
    isRetainingDebtor,
    isCaseDebtor,
    firstName,
    lastName,
    debtorType,
    lastRegCaseNo,
  } = retainingDebtor ?? {};

  return (
    <>
      {retainingDebtor && (
        <div className="mdb-col mdb-col-retaining">
          <div className="mdb-col-wrap">
            <div className="mdb-row mdb-row-header">
              <$Checkbox
                checked={true}
                disabled={true}
                className="mdb-checkbox"
              >
                <div className="d-flex flex-column">
                  <span>
                    {t(
                      "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.RETAINING_DEBTOR"
                    )}
                  </span>
                  {lastRegCaseNo && (
                    <span className="text-muted font-sm">
                      {t("US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.LAST_CASE_NO")}
                      :{" "}
                      <a
                        onClick={() =>
                          getRouteUrl.moduleRoute(
                            "collection",
                            `/case/${lastRegCaseNo}`
                          )
                        }
                      >
                        {lastRegCaseNo}
                      </a>
                    </span>
                  )}
                  {!isCaseDebtor && isRetainingDebtor && (
                    <span className="text-muted font-sm font-weight-normal">
                      {t(
                        "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.SUGGESTED_RETAIN_DEBTOR"
                      )}
                    </span>
                  )}
                  {isCaseDebtor && (
                    <span className="text-muted font-sm font-weight-normal">
                      {t("US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.CASE_DEBTOR")}
                    </span>
                  )}
                </div>
              </$Checkbox>
            </div>
            {DEBTOR_BASIC_INFO.map((propertyName, index) => (
              <div className="mdb-row" key={index}>
                {propertyName == PropertyNames.NAME && (
                  <Paragraph ellipsis={{ tooltip: true }} className="m-0">
                    {!firstName && !lastName && "-"}
                    {firstName && firstName} {lastName && lastName}
                  </Paragraph>
                )}
                {propertyName == PropertyNames.TYPE &&
                  (debtorType == DebtorType.PERSON
                    ? t("US.COLLECTION.CASE:EMPLOYER.PERSON")
                    : t("US.COLLECTION.CASE:EMPLOYER.COMPANY"))}
                {propertyName == PropertyNames.BIRTH_DATE && (
                  <$DateLabel
                    value={getFelidValue(retainingDebtor, propertyName)}
                  />
                )}
                {propertyName !== PropertyNames.NAME &&
                  propertyName !== PropertyNames.TYPE &&
                  propertyName !== PropertyNames.BIRTH_DATE && (
                    <Paragraph ellipsis={{ tooltip: true }} className="m-0">
                      {getFelidValue(retainingDebtor, propertyName)}
                    </Paragraph>
                  )}
              </div>
            ))}
            <div className="mdb-row mdb-row-header" />

            {DEBTOR_ADDRESS_INFO.map((propertyName, index) => (
              <div className="mdb-row" key={index}>
                <Paragraph ellipsis={{ tooltip: true }} className="m-0">
                  {getFelidValue(retainingDebtor, propertyName)}
                </Paragraph>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const mapState = (state: RootState) => {
  const { duplicateDebtors } = state;
  const { duplicatingDebtors } = duplicateDebtors;

  return {
    duplicatingDebtors,
  };
};

const connector = connect(mapState, {});

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(RetainDebtor);
